/**
 * The tools a pull request page's chat session offers its agent, on top of
 * every session's: a `pr_page` session (`pr-page-session.ts`), which runs the
 * chat in the page's sidebar. They port the sidebar's own chat tools
 * (`src/app/(chat)/api/chat/chat-tools/`) and keep their input field names, so
 * the page reads a call as it read theirs.
 *
 * - The page's actions (`PR_PAGE_ACTION_TOOLS`): the page acts when its
 *   transcript shows the call (`src/components/chat/code-session/`), as it
 *   acted on the sidebar tool's result: scroll to code, open a draft comment,
 *   mark files viewed. Showing code and marking files are live, so cubic first
 *   checks the files against what the page shows, as their sidebar tools did,
 *   and the agent learns of a file the pull request does not change; drafting
 *   a comment answers its fixed result.
 * - What cubic knows of the pull request, its review plan, the repository's
 *   custom agents, and documentation search are live tools: cubic answers each
 *   call with the sidebar tool's own output
 *   (`src/app/(chat)/api/chat/code-session/pr-page-tool-call.ts`). The sidebar
 *   chat's prompt also carried the pull request's description and changed
 *   files, which `pr_summary` adds to its sidebar tool's output.
 *
 * No Node imports: the session permissions read the names.
 */

import type { SessionToolDefinition } from '.';

import {
  CUSTOM_RULE_DESCRIPTION_MAX_LENGTH,
  CUSTOM_RULE_PATTERNS_MAX_LENGTH,
  CUSTOM_RULE_TITLE_MAX_LENGTH,
} from '@/lib/ai-review-settings/constants';

export const FOCUS_CODE_TOOL = 'focus_code';
export const DRAFT_PR_COMMENT_TOOL = 'draft_pr_comment';
export const SET_PR_FILE_VIEWED_TOOL = 'set_pr_file_viewed';
export const PR_SUMMARY_TOOL = 'pr_summary';
export const PR_REVIEW_PLAN_TOOL = 'pr_review_plan';
export const PR_CHANGED_FILES_TOOL = 'pr_changed_files';
export const PR_CHANGED_FILE_TOOL = 'pr_changed_file';
export const PR_SEARCH_TOOL = 'pr_search';
export const PR_REPO_MEMORY_TOOL = 'pr_repo_memory';
export const PR_THREADS_TOOL = 'pr_threads';
export const PR_THREAD_TOOL = 'pr_thread';
export const PR_CHECKS_TOOL = 'pr_checks';
export const LIST_CUSTOM_AGENTS_TOOL = 'list_custom_agents';
export const PROPOSE_CUSTOM_AGENT_CHANGE_TOOL = 'propose_custom_agent_change';
export const SEARCH_CODE_DOCS_TOOL = 'search_code_docs';

/** The tools whose calls cubic answers, while they run (`session-tool-proxy.ts`). */
export const PR_PAGE_LIVE_TOOLS = [
  FOCUS_CODE_TOOL,
  SET_PR_FILE_VIEWED_TOOL,
  PR_SUMMARY_TOOL,
  PR_REVIEW_PLAN_TOOL,
  PR_CHANGED_FILES_TOOL,
  PR_CHANGED_FILE_TOOL,
  PR_SEARCH_TOOL,
  PR_REPO_MEMORY_TOOL,
  PR_THREADS_TOOL,
  PR_THREAD_TOOL,
  PR_CHECKS_TOOL,
  LIST_CUSTOM_AGENTS_TOOL,
  PROPOSE_CUSTOM_AGENT_CHANGE_TOOL,
  SEARCH_CODE_DOCS_TOOL,
] as const;

export type PrPageLiveTool = (typeof PR_PAGE_LIVE_TOOLS)[number];

/** The page's actions: what the page does when its transcript shows the call. */
export const PR_PAGE_ACTION_TOOLS = [
  FOCUS_CODE_TOOL,
  DRAFT_PR_COMMENT_TOOL,
  SET_PR_FILE_VIEWED_TOOL,
] as const;

export type PrPageActionTool = (typeof PR_PAGE_ACTION_TOOLS)[number];

const EMPTY_INPUT = {
  type: 'object',
  additionalProperties: false,
  properties: {},
} as const;

const LINE_NUMBER = { type: 'integer', minimum: 1 } as const;

const SIDE = {
  type: 'string',
  enum: ['old', 'new'],
  description:
    'The diff side the lines are on: "new" for added or unchanged lines as the pull request leaves them, "old" for deleted lines as the base had them.',
} as const;

export const FOCUS_CODE_TOOL_DEFINITION = {
  name: FOCUS_CODE_TOOL,
  description: [
    'Scroll the pull request page the person is reading to a changed file, and highlight lines in its diff. Use it whenever they ask to see, show or find code in the pull request, when you explain a specific bug or implementation, and on each stop of a guided tour; for a file-level answer, give the file alone.',
    "It only navigates: read the code first, and highlight only lines you read in the file at the commit the page shows. Name the file by its path from the repository root, as `git diff --name-only` lists the pull request's changes; the page can show only files the pull request changes.",
    'Call it at most once per reply. When several places matter, show the most relevant one and name the others in your reply.',
  ].join('\n\n'),
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['filename'],
    properties: {
      filename: {
        type: 'string',
        minLength: 1,
        description: 'The changed file, e.g. "src/components/Button.tsx".',
      },
      side: SIDE,
      lineNumber: { ...LINE_NUMBER, description: 'One line to scroll to and highlight.' },
      lineNumbers: {
        type: 'array',
        items: LINE_NUMBER,
        description:
          'The lines to highlight, e.g. [10, 11, 12] for a block; a whole function or class when you show one.',
      },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const DRAFT_PR_COMMENT_TOOL_DEFINITION = {
  name: DRAFT_PR_COMMENT_TOOL,
  description: [
    "Open a review comment on a line of the pull request's diff as a draft on the page, for the person to edit and post themselves. Nothing reaches GitHub until they post it.",
    'Draft one only when they ask for a draft to edit and post themselves, and at most one per reply. To suggest a code change, put it in the comment as a ```suggestion block holding the replacement lines.',
  ].join('\n\n'),
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['filename', 'lineNumber', 'side', 'comment'],
    properties: {
      filename: {
        type: 'string',
        minLength: 1,
        description: 'The changed file the comment is on.',
      },
      lineNumber: {
        ...LINE_NUMBER,
        description: 'The line the comment is on, or the last line of a range.',
      },
      start_line: {
        ...LINE_NUMBER,
        description: 'The first line of a multi-line comment; omit for one line.',
      },
      side: SIDE,
      comment: { type: 'string', description: 'The comment in Markdown.' },
    },
  },
  needs: 'conversation',
  result:
    'The comment is open as a draft on the pull request page. Tell the person it is ready for them to review and post.',
} as const satisfies SessionToolDefinition;

export const SET_PR_FILE_VIEWED_TOOL_DEFINITION = {
  name: SET_PR_FILE_VIEWED_TOOL,
  description:
    'Mark changed files viewed or unviewed on the pull request page, as the person\'s own Viewed checkboxes do. Use it only when they ask. For "this file", leave out filenames and the page marks the file they are looking at. Never mark every file at once.',
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['viewed'],
    properties: {
      filenames: {
        type: 'array',
        items: { type: 'string', minLength: 1 },
        minItems: 1,
        description: 'The changed files to mark; omit for the file the person is looking at.',
      },
      viewed: { type: 'boolean', description: 'true to mark viewed, false to mark unviewed.' },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_SUMMARY_TOOL_DEFINITION = {
  name: PR_SUMMARY_TOOL,
  description:
    "Read what cubic knows of this pull request: its identity, its description in its author's words, its changed files with their size, counts of reviews, comments, threads and checks, how fresh they are, the file the person is looking at, and the top unresolved review threads. Start here for questions about the pull request as a whole, such as what it changes and why, or what is still open.",
  input: EMPTY_INPUT,
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_REVIEW_PLAN_TOOL_DEFINITION = {
  name: PR_REVIEW_PLAN_TOOL,
  description:
    "Read cubic's review plan for this pull request: its changed files in groups, in the order to review them, each with its review priority and the review decisions cubic recorded on it. Read it before a guided tour, and when the person asks what to review first.",
  input: EMPTY_INPUT,
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_CHANGED_FILES_TOOL_DEFINITION = {
  name: PR_CHANGED_FILES_TOOL,
  description:
    'List the changed files in review order, with the review threads, reviews, discussion and failing checks on each.',
  input: EMPTY_INPUT,
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_CHANGED_FILE_TOOL_DEFINITION = {
  name: PR_CHANGED_FILE_TOOL,
  description:
    'Read what cubic prepared for one changed file: its threads, reviews and checks, and its diff as the page shows it. To read or explain its code, read the file from the workspace instead, which gives line numbers.',
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['path'],
    properties: {
      path: { type: 'string', description: "The changed file's path or filename." },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_SEARCH_TOOL_DEFINITION = {
  name: PR_SEARCH_TOOL,
  description:
    "Search what cubic prepared about this pull request: its summary, commits, changed files, review threads, reviews, discussion and checks. Use pr_repo_memory for the repository's memory.",
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['query'],
    properties: {
      query: { type: 'string', description: 'What to look for, matched without case.' },
      kinds: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['summary', 'commit', 'thread', 'review', 'discussion', 'check', 'file'],
        },
        description: 'Only these kinds of artifact.',
      },
      limit: { type: 'integer', minimum: 1, description: 'At most this many results.' },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_REPO_MEMORY_TOOL_DEFINITION = {
  name: PR_REPO_MEMORY_TOOL,
  description:
    "Read the repository's memory in cubic: what it learned of the team's review preferences, senior reviewers' guidance and the codebase. Use it when the person asks about memory, learnings, team preferences or review style, or whether such guidance applies here.",
  input: {
    type: 'object',
    additionalProperties: false,
    properties: {
      query: {
        type: 'string',
        description: 'What to look for; leave it out to list the stored learnings.',
      },
      limit: { type: 'integer', minimum: 1, description: 'At most this many learnings.' },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_THREADS_TOOL_DEFINITION = {
  name: PR_THREADS_TOOL,
  description:
    "List the pull request's review threads, unresolved ones unless you ask for others, optionally on one changed file. Use it when the person asks what reviewers said, what is unresolved, or whether something was already raised.",
  input: {
    type: 'object',
    additionalProperties: false,
    properties: {
      status: { type: 'string', enum: ['all', 'unresolved', 'resolved'] },
      path: { type: 'string', description: 'Only threads on this changed file.' },
      limit: { type: 'integer', minimum: 1, description: 'At most this many threads.' },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_THREAD_TOOL_DEFINITION = {
  name: PR_THREAD_TOOL,
  description:
    'Read one review thread in full: every comment, whether it is resolved or outdated, and the code it is on.',
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['threadId'],
    properties: {
      threadId: { type: 'string', description: "The thread's id, as pr_threads lists it." },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_CHECKS_TOOL_DEFINITION = {
  name: PR_CHECKS_TOOL,
  description:
    "Read the pull request's checks: how many pass, fail or wait, and the details of each failing and pending one. Use it when the person asks what CI is blocking.",
  input: EMPTY_INPUT,
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const LIST_CUSTOM_AGENTS_TOOL_DEFINITION = {
  name: LIST_CUSTOM_AGENTS_TOOL,
  description:
    "List the repository's custom agents: the review checks cubic enforces on its pull requests, and whether each can be changed from this chat. List them before proposing a change to one.",
  input: EMPTY_INPUT,
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

const PATTERNS = {
  type: ['string', 'null'],
  maxLength: CUSTOM_RULE_PATTERNS_MAX_LENGTH,
  description: 'Glob patterns, one per line; null clears them.',
} as const;

export const PROPOSE_CUSTOM_AGENT_CHANGE_TOOL_DEFINITION = {
  name: PROPOSE_CUSTOM_AGENT_CHANGE_TOOL,
  description: [
    "Propose creating or updating one of the repository's custom agents, the review checks cubic enforces on every pull request. The page shows the proposal as a card the person applies or cancels; nothing changes until they apply it.",
    "Propose one only when the person asks to create, enforce, flag, stop allowing or update such a check, never for an explanation or a one-off look at this pull request. Background guidance belongs in repository memory, not in a custom agent. A create needs a title and a description; an update needs the agent's id from list_custom_agents and at least one change.",
  ].join('\n\n'),
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['action'],
    properties: {
      action: { type: 'string', enum: ['create', 'update'] },
      agentId: { type: 'string', minLength: 1, description: 'The agent to update.' },
      title: { type: 'string', minLength: 1, maxLength: CUSTOM_RULE_TITLE_MAX_LENGTH },
      description: {
        type: 'string',
        minLength: 1,
        maxLength: CUSTOM_RULE_DESCRIPTION_MAX_LENGTH,
        description: 'What the agent checks, in the words a reviewer would use.',
      },
      enabled: { type: 'boolean' },
      includePatterns: { ...PATTERNS, description: `Files it checks. ${PATTERNS.description}` },
      excludePatterns: { ...PATTERNS, description: `Files it skips. ${PATTERNS.description}` },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const SEARCH_CODE_DOCS_TOOL_DEFINITION = {
  name: SEARCH_CODE_DOCS_TOOL,
  description:
    'Search the web for current documentation of libraries and frameworks, and code examples. Use neutral, factual queries. Answers with highlights from the top results.',
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['query'],
    properties: {
      query: {
        type: 'string',
        minLength: 1,
        description: 'What to look up, in plain words.',
      },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;

export const PR_PAGE_TOOL_DEFINITIONS = [
  FOCUS_CODE_TOOL_DEFINITION,
  DRAFT_PR_COMMENT_TOOL_DEFINITION,
  SET_PR_FILE_VIEWED_TOOL_DEFINITION,
  PR_SUMMARY_TOOL_DEFINITION,
  PR_REVIEW_PLAN_TOOL_DEFINITION,
  PR_CHANGED_FILES_TOOL_DEFINITION,
  PR_CHANGED_FILE_TOOL_DEFINITION,
  PR_SEARCH_TOOL_DEFINITION,
  PR_REPO_MEMORY_TOOL_DEFINITION,
  PR_THREADS_TOOL_DEFINITION,
  PR_THREAD_TOOL_DEFINITION,
  PR_CHECKS_TOOL_DEFINITION,
  LIST_CUSTOM_AGENTS_TOOL_DEFINITION,
  PROPOSE_CUSTOM_AGENT_CHANGE_TOOL_DEFINITION,
  SEARCH_CODE_DOCS_TOOL_DEFINITION,
] as const satisfies readonly SessionToolDefinition[];

export type PrPageToolName = (typeof PR_PAGE_TOOL_DEFINITIONS)[number]['name'];

/**
 * The sidebar chat's tool each page tool ports (`src/app/(chat)/api/chat/chat-tools/`): cubic
 * answers a live call with that tool's output (`pr-page-tool-call.ts`), and the page shows each
 * call as that tool (`src/components/chat/code-session/session-chat-messages.ts`). The review
 * plan has none: the sidebar chat's prompt carried it.
 */
export const PR_PAGE_SIDEBAR_TOOLS = {
  [FOCUS_CODE_TOOL]: 'focusCode',
  [DRAFT_PR_COMMENT_TOOL]: 'addPrComment',
  [SET_PR_FILE_VIEWED_TOOL]: 'setPrFileViewed',
  [PR_SUMMARY_TOOL]: 'readPrSummary',
  [PR_REVIEW_PLAN_TOOL]: null,
  [PR_CHANGED_FILES_TOOL]: 'listChangedFiles',
  [PR_CHANGED_FILE_TOOL]: 'readChangedFile',
  [PR_SEARCH_TOOL]: 'searchPrArtifacts',
  [PR_REPO_MEMORY_TOOL]: 'readRepoMemory',
  [PR_THREADS_TOOL]: 'listThreads',
  [PR_THREAD_TOOL]: 'readThread',
  [PR_CHECKS_TOOL]: 'readChecksSummary',
  [LIST_CUSTOM_AGENTS_TOOL]: 'listCustomAgents',
  [PROPOSE_CUSTOM_AGENT_CHANGE_TOOL]: 'proposeCustomAgentChange',
  [SEARCH_CODE_DOCS_TOOL]: 'get_code_context_exa',
} as const satisfies Record<PrPageToolName, string | null>;

/** Every tool only a pull request page's chat session is offered. */
export const PR_PAGE_TOOL_NAMES: ReadonlySet<string> = new Set(
  PR_PAGE_TOOL_DEFINITIONS.map((tool) => tool.name),
);
